import { isReddit, isSpotify } from '@aleph/shared-components';
import useSupergridSettings from 'wb-spotify/app/routes/superGrid/hooks/useSupergridSettings';
import dayjs from 'dayjs';
import { ReportDateType } from 'wb-reddit/graphql/generated';
import { TIME_REGEX, SpotifyReportSchedule } from 'wb-spotify/app/common/utils';
import { useCallback } from 'react';
import useCreateRedditReport from 'wb-reddit/app/routes/reports/create/useCreateRedditReport';
import { getReportClientIds } from 'wb-reddit/app/routes/reports/utils';
import { RedditReport } from 'wb-reddit/app/routes/reports/components/report';
import { useTranslation } from 'react-i18next';
import { LIFE_TIME_METRICS } from 'wb-spotify/app/routes/superGrid/statistics/utils';

function useSetCreateRedditReportData() {
	const { t } = useTranslation();

	const {
		setFilters, setBreakdowns, setMetrics, setDates,
		setSchedule, setUserIds, setUserGroupIds, setTime, setPrivateUserGroupIds,
		setReportId, setName,
	} = useCreateRedditReport();

	const { toggleLifetimeStats } = useSupergridSettings();

	return useCallback(
		(report: RedditReport) => {
			const clientIds = getReportClientIds(report.reportApi?.reportApiFiltersOptions);
			const key = isReddit() ? 'clientId' : 'userGroupId';

			if (isSpotify()) {
				// @ts-ignore TODO might not work if metric is not in the list
				const isLifetimeStats = LIFE_TIME_METRICS.some(metric => report.reportMetrics?.includes?.(metric))
                    // @ts-ignore
                    || report.reportDimensions?.every(breakdown => breakdown !== 'date');

				if (isLifetimeStats) {
					toggleLifetimeStats();
				}
			}

			if ((clientIds.length > 0 && report?.reportId) || isSpotify()) {
				setReportId(report?.reportId);
				setFilters({
					[key]: [{ value: clientIds }],
				});
				setName(report.reportName ?? t('re: reports.editing', 'Editing report'));

				if (isReddit()) {
					setBreakdowns((report.reportApi?.reportApiDimensions as string[] ?? []).filter(
						breakdown => !['agencyAccountId', 'adAccountId', 'campaignId', 'groupId', 'adId'].includes(breakdown),
					));
				} else {
					// @ts-ignore
					setBreakdowns((report.reportDimensions));
				}

				if (isReddit()) {
					setMetrics(report.reportApi?.reportApiMetrics as string[] ?? []);
				} else {
					// @ts-ignore
					setMetrics(report.reportMetrics);
				}

				if (isReddit()) {
					setUserGroupIds(report.userGroups?.map(userGroup => userGroup.id ?? '') ?? []);
				} else {
					setUserGroupIds(clientIds ?? []);
					// @ts-ignore
					setPrivateUserGroupIds(report.userGroupId ?? []);
				}

				setUserIds(report.users?.map(user => user?.id ?? '') ?? []);

				const dateRangeValues = report.reportDateRangeOptions?.dateRangeValue ?? [];

				if (dateRangeValues?.length > 0) {
					setDates({ from: dayjs(dateRangeValues?.[0]), to: dayjs(dateRangeValues?.[1]) });
				}

				if (isReddit()) {
					setSchedule(report.reportDateType ?? ReportDateType.Custom);
				} else {
					setSchedule(report.reportSchedule);
				}

				if (isSpotify() && TIME_REGEX.test(report.reportSchedule)) {
					setSchedule(SpotifyReportSchedule.OnlyOnce);
					setTime(report.reportSchedule);
				}
			}
		},
		[
			setFilters, setBreakdowns, setMetrics, setDates,
			setSchedule, setUserIds, setUserGroupIds, setTime, setPrivateUserGroupIds,
			setReportId, setName, toggleLifetimeStats,
		],
	);
}

export default useSetCreateRedditReportData;
